import * as QA from 'shared/utils/queryAnalysis';
import { areStringsInsensitivelyEqual, getOrCreate, asString } from 'shared/utils/utils';
import { foldIndexesToSpans } from 'shared/excel/span';
import { PUSH_FLOWSTEP_RESULT_HEADER, PUSH_FLOWSTEP_RESULT_HEADERS } from 'shared/excel/runtime/flowstepExecution';
import { MultipleErrorsError, isPureObject, splitArrayToChunksByLength } from 'shared/utils/utils';


class ValuePlaceholder {
  value = null;
};


export class NamesConverter {
  objectNamesToRecords = new Map();
  rowIndexesToRecords = new Map();
  objectNamesToFieldsProps = new Map();

  /// Deprecated
  static errorsToValues(objectsWithErrors, timestamp, headers = PUSH_FLOWSTEP_RESULT_HEADERS) {
    const count = headers.length;
    const dateIndex = headers.findIndex(x => x.key === PUSH_FLOWSTEP_RESULT_HEADER.Date.key);
    const resultIndex = headers.findIndex(x => x.key === PUSH_FLOWSTEP_RESULT_HEADER.Result.key);

    return objectsWithErrors.map(x => {
      const v = Array(count).fill('');
      v[dateIndex] = timestamp;
      v[resultIndex] = `Record cannot be uploaded. ${x.error}`;
      return v;
    });
  }

  constructor({ queryLoader, objectLoader }) {
    this.queryLoader = queryLoader;
    this.objectLoader = objectLoader;
  }

  reset() {
    this.objectNamesToRecords.clear();
    this.rowIndexesToRecords.clear();
  }

  isEmpty() {
    return !this.objectNamesToRecords.size;
  }

  async getConverter(columnData) {
    const { rawField, columnIndex, objectName, lookupObject } = columnData;
    const isRelationship = !!lookupObject || QA.isRelationshipField(rawField);

    //necessary, because the field name is used as the map key
    if (lookupObject) {
      lookupObject.name = lookupObject.name.toLowerCase();
    }

    // It may turn out that there are objects where there are no 'Name' field and we cannot 
    // use another one instead (as in the case of polymorphic 'Case' object, where we can use its field 'caseNumber')
    const objectNames = isRelationship
      ? (lookupObject
        ? [lookupObject.objectName]
        : QA.getRelationshipReferences(rawField))
      : [objectName];
   
    const objectsToSearchIn = await this.loadObjectsProps(objectNames, lookupObject?.name)
    if (!objectsToSearchIn.length) {
      return (value, rowIndex) => ({ value });
    }

    const addRecord = (recordName, rowIndex, record) => {
      objectsToSearchIn.forEach(({ objectName, lookupFieldName }) => {
        const recordsByObjectName = getOrCreate(this.objectNamesToRecords, objectName, () => new Map());
        const recordsByLookupField = getOrCreate(recordsByObjectName, lookupFieldName, () => new Map());
        const recordsByName = getOrCreate(recordsByLookupField, recordName, () => []);
        record.lookupFieldName = lookupFieldName;
        recordsByName.push(record);
        const recordsByRowIndex = getOrCreate(this.rowIndexesToRecords, rowIndex, () => []);
        recordsByRowIndex.push(record);
      });
    };

    const addToQuery = isRelationship
      ? (value, rowIndex) => {
        const { name: fieldName, relationshipName } = rawField;
        const isPoly = QA.isPolymorphicField(rawField);
        const valuePlaceholder = new ValuePlaceholder();
        const record = { 
          rowIndex, columnIndex, name: value, fieldName, 
          polymorphicRelationshipName: isPoly ? relationshipName : null, 
          objectName: lookupObject?.objectName || (isPoly ? null : objectsToSearchIn[0].objectName),
          valuePlaceholder,
        };
        addRecord(value, rowIndex, record);
        return valuePlaceholder;
      }
      : (value, rowIndex) => {
        const valuePlaceholder = new ValuePlaceholder();
        const record = { 
          rowIndex, columnIndex, name: value, fieldName: rawField.name, objectName, 
          valuePlaceholder,
        };
        addRecord(value, rowIndex, record);
        return valuePlaceholder;
      }

    return (initialValue, rowIndex) => {
      const value = asString(initialValue);
      if (!!value && !QA.isSalesforceId(value)) {
        return { value: addToQuery(value, rowIndex) };
      }
      return { value };
    };
  }

  async loadObjectsProps(objectNames, lookupFieldName) {
    const res = [];
    for (const objName of objectNames) {
      const props = await this.loadObjectProps(objName, lookupFieldName);
      if (props) {
        res.push({ objectName: objName, lookupFieldName: props.nameFieldName })
      }
    }  
     return res;
  }

  async loadObjectProps(objectName, lookupFieldName = QA.getNameFieldName(objectName)) {
    const fieldsProps = getOrCreate(this.objectNamesToFieldsProps, objectName, () => new Map());
    let res = fieldsProps.get(lookupFieldName);
    if (!res) {
        const { fields } = await this.objectLoader(objectName);
        const rawField = fields.find(x => areStringsInsensitivelyEqual(x.name, lookupFieldName));
        if (rawField) {
            res = { rawField, isGroupable: rawField.groupable, nameFieldName: rawField.name.toLowerCase(), nameFieldLabel: rawField.label.toLowerCase() };
            fieldsProps.set(lookupFieldName, res);
        }      
    }
    return res;
  }

  /// Deprecated
  async replaceNamesWithIds(spansTree, objects) {
    if (this.isEmpty()) {
      return { objectsToReject: [], spansTreeToReject: null, objectsToUpload: objects, spansTreeToUpload: spansTree };
    }

    function replacePlaceholders(object, key, value) {
      if (value instanceof ValuePlaceholder) {
        object[key] = value.value;
      }
      else if (Array.isArray(value)) {
        replacePlaceholdersInArray(value);
      }
      else if (isPureObject(value)) {
        replacePlaceholdersInObject(value);
      }
    }

    function replacePlaceholdersInArray(arr) {
      arr.forEach((v, index) => {
        replacePlaceholders(arr, index, v);
      });
    };

    function replacePlaceholdersInObject(object) {
      for (const [key, value] of Object.entries(object)) {
        replacePlaceholders(object, key, value);
      }     
    }

    const { spansToReject, toUpload, toReject } = await this.queryNames();
    const { toPick: spansTreeToReject, toLeft: spansTreeToUpload } = spansTree.pickByCriteriaSpans(spansToReject);

    toUpload.forEach(({ rowIndex, columns }) => {
      const object = objects[rowIndex];
      replacePlaceholdersInObject(object);
    });

    for (let i = spansToReject.length - 1; i >= 0; --i) {
      const { index, count } = spansToReject[i];
      objects.splice(index, count);
    }
    
    return { objectsToReject: toReject, spansTreeToReject, objectsToUpload: objects, spansTreeToUpload };
  }

  async resolveNames(values) {
    if (this.isEmpty()) {
      return [];
    }

    function replacePlaceholders(object, key, value) {
      if (value instanceof ValuePlaceholder) {
        object[key] = value.value;
      }
      else if (Array.isArray(value)) {
        replacePlaceholdersInArray(value);
      }
      else if (isPureObject(value)) {
        replacePlaceholdersInObject(value);
      }
    }

    function replacePlaceholdersInArray(arr) {
      arr.forEach((v, index) => {
        replacePlaceholders(arr, index, v);
      });
    };

    function replacePlaceholdersInObject(object) {
      for (const [key, value] of Object.entries(object)) {
        replacePlaceholders(object, key, value);
      }     
    }

    const objects = await this.executeQueries();
    return objects.filter(({ error, rowIndex, }) => {
      if (!error) {
        replacePlaceholdersInObject(values[rowIndex]);
      }
      return !!error;
    });
  }

  /// Deprecated
  async queryNames() {
    const queries = this.makeQueries();
    const queriesResult = await this.queryLoader(queries.map(x => x.query));
    this.processResponse(queries, queriesResult);
    const result = this.collectObjects();
    this.reset();
    return result;
  }

  async executeQueries() {
    const queries = this.makeQueries();
    const queriesResult = await this.queryLoader(queries.map(x => x.query));
    this.processResponse(queries, queriesResult);
    return this.#responseToObjects();
  }
 
  makeQueries() {
    const queries = [];
    this.objectNamesToRecords.forEach((lookupFieldsToRecords, objectName) => {
      lookupFieldsToRecords.forEach((namesToRecords, lookupFieldName) => {
        const namesToFind = Array.from(namesToRecords.keys());
        const objectProps = this.objectNamesToFieldsProps.get(objectName).get(lookupFieldName);
        const { isGroupable } = objectProps;
        const namesChunks = isGroupable
          //In SOQL you cannot use grouping by more than 2000 values
          ? splitArrayToChunksByLength(namesToFind, 2000)
          : [namesToFind];

        namesChunks.forEach(namesChunk => {
          const query = this.makeQuery(objectName, namesChunk, objectProps);
          queries.push({ objectName, lookupFieldName, query });
        });
      });
    });
    return queries;
  }

  makeQuery(objectName, namesToFind, { nameFieldName, isGroupable }) {
    const where = `WHERE ${nameFieldName} IN (${namesToFind.map(x => `'${QA.escapeValue(x)}'`).join(', ')})`;
    if (isGroupable) {
      // We cannot simply return id from an aggregated query, so use max(id) instead
      const select = `SELECT Max(id), count(id), ${nameFieldName} FROM ${objectName}`;
      const groupBy = `GROUP BY ${nameFieldName}`;
      return `${select} ${where} ${groupBy}`;
    }

    const select = `SELECT Id, ${nameFieldName} FROM ${objectName}`;
    return `${select} ${where}`;
  }

  processResponse(queries, results) {
    const errors = results.reduce((out, response, index) => {
      const { result, error } = response;
      if (error) {
        out.push(error);
        return out;
      }

      const { objectName, lookupFieldName } = queries[index];
      const objectLookupFields = this.objectNamesToFieldsProps.get(objectName);
      const objectProps = objectLookupFields.get(lookupFieldName);
      const { nameFieldLabel } = objectProps;
      const resultNames = this.resultToObjects(result, objectProps);
      const recordsByObjectName = this.objectNamesToRecords.get(objectName);
      const recordsByLookupField = recordsByObjectName.get(lookupFieldName);

      resultNames.forEach(resultRecord => {
        const { id, count, name } = resultRecord;
        // May be undefined because IN operator is case-insensitive and here the name could be 'abc' whereas we have searched for 'Abc'
        const recordsByName = recordsByLookupField.get(name);
        if (!recordsByName) { 
          return;
        }
        // TODO: add to objects cache
        const toAssing = count !== 1
          ? { error: `Unable to uniquely identify a record with ${nameFieldLabel} '${name}' of object '${objectName}': there are ${count} records with this ${nameFieldLabel}` }
          : { id, objectName }; // Write down the name of the object for polymorphic records
        recordsByName.forEach(record => {
          const { objectName: recordObjectName, polymorphicRelationshipName } = record;
          if (!record.error) {
            record.id // Polymorphic relationship, one name is assigned to different objects
              ? Object.assign(record, { id: null, error: `Unable to uniquely identify a record with ${nameFieldLabel} '${name}' of a polymorphic relationship '${polymorphicRelationshipName}': records of '${objectName}' and '${recordObjectName}' objects have the same ${nameFieldLabel}` })
              : Object.assign(record, toAssing);
          }
        });
      });

      return out;
    }, []);

    if (errors.length) {
      throw new MultipleErrorsError(errors, `Unable to execute ${errors.length} queries to convert names to Ids`);
    }
  }

  /// Deprecated
  collectObjects() {
    const objects = [];
    this.rowIndexesToRecords.forEach((records, rowIndex) => {
      const object = records.reduce((out, { id: value, error, columnIndex, objectName, lookupFieldName, name, fieldName, polymorphicRelationshipName, valuePlaceholder }) => {
        if (value) {
          valuePlaceholder.value = value;
          out.columns.push({ columnIndex, value, fieldName });
        }
        else {
          const nameFieldLabel = this.objectNamesToFieldsProps.get(objectName).get(lookupFieldName).nameFieldLabel;
          const type = polymorphicRelationshipName ? `polymorphic relationship '${polymorphicRelationshipName}'` : `object '${objectName}'`;
          out.errors.push(error ?? `Unable to find a record with ${nameFieldLabel} '${name}' of ${type}`);
        }
        return out;
      }, { rowIndex, columns: [], errors: [] });

      object.error = object.errors.join('. ');
      delete object.errors;

      objects.push(object);
    });

    objects.sort((a, b) => a.rowIndex - b.rowIndex);

    const shouldReject = (x) => !!x.error;
    const spansToReject = foldIndexesToSpans(objects.reduce((out, x) => {
      shouldReject(x) && out.push(x.rowIndex);
      return out;
    }, []));
    const toUpload = objects.filter(x => !shouldReject(x));
    const toReject = objects.filter(shouldReject);

    return { spansToReject, toUpload, toReject };
  }

  #responseToObjects() {
    const objects = [];
    this.rowIndexesToRecords.forEach((records, rowIndex) => {
      const objectsInRow = records.map(({ id: value, error, columnIndex, objectName, lookupFieldName, name, polymorphicRelationshipName, valuePlaceholder }) => {
        const obj = { rowIndex, columnIndex }; 
        if (value) {
          valuePlaceholder.value = value;
          return { ...obj, value };
        }

        const nameFieldLabel = this.objectNamesToFieldsProps.get(objectName).get(lookupFieldName).nameFieldLabel;
        const type = polymorphicRelationshipName ? `polymorphic relationship '${polymorphicRelationshipName}'` : `object '${objectName}'`;
        error ??= `Unable to find a record with ${nameFieldLabel} '${name}' of ${type}`;
        return { ...obj, error };
      });

      objects.push(...objectsInRow);
    });

    this.reset();
    return objects;
  }

  resultToObjects(result, { isGroupable }) {
    return isGroupable
      ? this.arrayToObjects(result, ['id', 'count', 'name'])
      : this.groupResultObjects(result);
  }

  groupResultObjects(result) {
    const objects = this.arrayToObjects(result, ['id', 'name']);
    const objectsMap = new Map();
    objects.forEach(({ id, name }) => {
      const data = getOrCreate(objectsMap, name, () => ({ id, count: 0 }));
      data.count += 1;
    });

    const grouped = [];
    objectsMap.forEach((value, key) => grouped.push({ name: key, ...value }));
    return grouped;
  }

  arrayToObjects(result, fieldsToReturn) {
    return result.map(row => row.reduce((out, v, i) => {
      out[fieldsToReturn[i]] = v;
      return out;
    }, {}));
  }
};

